import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Eyebrow, FadeIn } from "./Reveal";
import { EXPERIENCES } from "@/data/experiences";

const TOURS = EXPERIENCES.map((e) => ({
  slug: e.slug,
  title: e.title,
  price: e.details.find((d) => d.label === "Price").value,
  duration: e.details.find((d) => d.label === "Duration").value,
  capacity: e.details.find((d) => d.label === "Max capability").value,
  image: e.cardImage,
  blurb: e.short,
}));

function TourCard({ tour, index }) {
  return (
    <FadeIn delay={index * 0.1} className="h-full">
      <Link
        to={`/experiences/${tour.slug}`}
        data-testid={`tour-card-${index + 1}`}
        className="group flex h-full flex-col border border-white/5 bg-navy-800 transition-colors duration-500 hover:border-gold/40"
      >
        <div className="relative aspect-[3/4] overflow-hidden">
          <img
            src={tour.image}
            alt={tour.title}
            className="h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.07]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-navy-950/90 via-navy-950/10 to-navy-950/20" />
          <span className="absolute right-4 top-4 bg-gold px-3 py-1.5 text-[11px] font-semibold tracking-[0.12em] text-navy-800">
            {tour.price}
          </span>
          <span className="absolute left-4 top-4 border border-gold/50 bg-navy-950/60 px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] text-gold backdrop-blur-sm">
            {String(index + 1).padStart(2, "0")}
          </span>
        </div>
        <div className="flex flex-1 flex-col p-6">
          <h3 className="font-serif text-2xl leading-tight text-ivory">{tour.title}</h3>
          <p className="mt-3 flex-1 text-sm font-light leading-relaxed text-mist">{tour.blurb}</p>
          <div className="mt-6 flex items-center justify-between border-t border-white/10 pt-4 text-[11px] uppercase tracking-[0.18em] text-ivory/60">
            <span>{tour.duration}</span>
            <span>{tour.capacity}</span>
          </div>
          <span className="mt-4 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-gold">
            View Details
            <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1.5" />
          </span>
        </div>
      </Link>
    </FadeIn>
  );
}

export default function Tours() {
  return (
    <section id="experiences" className="bg-navy-950 py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <Eyebrow>Tours &amp; Experiences</Eyebrow>
            <FadeIn delay={0.1}>
              <h2 className="mt-6 max-w-xl font-serif text-4xl font-medium leading-[1.05] tracking-tight text-ivory sm:text-5xl lg:text-6xl">
                Adventures for <em className="italic text-gold">every</em> explorer
              </h2>
            </FadeIn>
          </div>
          <FadeIn delay={0.2}>
            <p className="max-w-md text-sm font-light leading-relaxed text-mist sm:text-base">
              Three ways to cross Clew Bay and one unbeatable way to warm up. Every tour is
              guided, kitted out and built around your group.
            </p>
          </FadeIn>
        </div>

        <div className="mt-16 grid gap-6 sm:grid-cols-2 xl:grid-cols-4">
          {TOURS.map((tour, i) => (
            <TourCard key={tour.title} tour={tour} index={i} />
          ))}
        </div>

        <FadeIn delay={0.15}>
          <p className="mt-10 border-t border-white/10 pt-6 text-[12px] uppercase tracking-[0.18em] text-ivory/50">
            All tours depart from Rosmoney Pier, 12 minutes from Westport town &middot; add the
            Ultimate Private Island Picnic Tour to any trip for &euro;20pp extra
          </p>
        </FadeIn>
      </div>
    </section>
  );
}
